/**
 * useChannel — the React bridge to the channel. Connects the ws at auth, keeps
 * the announcement stream (history replay + live), applies deltas, and exposes
 * the four tools (announce/search/upload/download). The whole client renders
 * from `announcements`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { channel, type ConversationFeedItem } from "./client";
import { feedStore } from "./feedStore";
import { getCachedArtifact, putCachedArtifact } from "./artifactCache";
import { artifactUpgradeStore } from "./artifactUpgradeStore";
import { announcementArtifactMachineIdOf, artifactMachineRefOf } from "./artifactMachine";
import { AnnouncementSchema, type Announcement } from "./contract";
import type { ArtifactInteractionInput } from "./artifactInteraction";

// THE HARNESS AUDIT LANE ATOM (sci//harness:audit:@terminal**): an audit ROW's
// own declaration. The pane rendering these rows lives IN the artifact (the
// world-owned wrapper); the shell only keeps an audit row from ever opening a
// stream box.
const HARNESS_AUDIT_TERMINAL_ATOM = "sci//harness:audit:@terminal**";
const carriesHarnessAudit = (a: Announcement): boolean => {
  const spellings: string[] = [];
  for (const r of [...(a.ref ?? []), ...(a.refs ?? [])]) {
    if (typeof r === "string") spellings.push(r);
    else if (isRecord(r) && typeof r.path === "string") spellings.push(String(r.path));
  }
  return spellings.includes(HARNESS_AUDIT_TERMINAL_ATOM);
};

const nextThenRefsOf = (a: Announcement): string[] => (
  (a.next ?? [])
    .flatMap((next) => Array.isArray((next as { then?: unknown[] })?.then) ? (next as { then: unknown[] }).then : [])
    .map((ref) => String((ref as { id?: unknown })?.id ?? (ref as { ref?: unknown })?.ref ?? ref))
    .filter(Boolean)
);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

const UPLOAD_CHUNK_BYTES = 128 * 1024;

// ── artifact content identity ────────────────────────────────────────────────
// The content key a seed carries for cache addressing.
// TODO(world leg, sweep §2): prefer the served face body FINGERPRINT
// (the content-addressed interface shape `sci//artifact:interface:@shape`
// on the LIST face) once the
// world serves it on every list/feed projection — until then the key is the
// seed's EXISTING shape/version identity (the fold bumps machine.version when
// content changed, so a bump correctly misses the cache). Never a timestamp
// heuristic.
const artifactSeedFingerprintOf = (artifact: unknown): string => {
  const a = artifact as {
    fingerprint?: unknown;
    shape?: unknown;
    machine?: { fingerprint?: unknown; shape?: unknown; version?: unknown };
  } | undefined;
  const declared = String(a?.fingerprint ?? a?.machine?.fingerprint ?? "");
  if (declared) return declared;
  const shape = String(a?.shape ?? a?.machine?.shape ?? "");
  const version = String(a?.machine?.version ?? "");
  return shape || version ? `${shape}:${version}` : "";
};

const blobToBase64 = async (blob: Blob): Promise<string> => {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let bin = "";
  for (let j = 0; j < bytes.length; j++) bin += String.fromCharCode(bytes[j]);
  return btoa(bin);
};

const uploadPartId = (conversationId: string, file: File, index: number): string =>
  `${conversationId}:${file.name}:${file.size}:${file.lastModified}:${index}`;

// ── chronological order ───────────────────────────────────────────────────────
// World stamps announcement timestamps at SECOND precision (microsecond=0), so
// every row created in the same second carries an IDENTICAL timestamp string.
// localeCompare then has no tiebreak and the stable sort falls back to insertion
// order (history hydrates newest-first) — same-second messages rendered REVERSED
// (newest on top). Order by normalized epoch, then by the world serial id
// (ascending = world creation order) so same-second rows read oldest-first.
const NUMERIC_ID = /^\d+$/;
const epochOf = (timestamp: string): number => {
  const s = String(timestamp || "");
  // timestamps reach the client tz-naive (outlet strips Z); normalize to UTC.
  const v = Date.parse(/[zZ]$|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`);
  return Number.isFinite(v) ? v : 0;
};
// optimistic (client-minted, non-numeric id) rows are the FRESHEST send — they
// sort AFTER same-second world rows until the world echo gives them a serial id.
const serialOf = (id: string): number => NUMERIC_ID.test(id) ? Number(id) : Number.POSITIVE_INFINITY;
const byChronology = (x: Announcement, y: Announcement): number =>
  epochOf(x.timestamp) - epochOf(y.timestamp) || serialOf(x.id) - serialOf(y.id);

// Significant fractional-second digits, e.g. "…09.957" → 3, "…09.000"/"…09" → 0.
const subSecondPrecision = (timestamp: string): number => {
  const m = /\.(\d+)/.exec(String(timestamp || ""));
  return m ? m[1].replace(/0+$/, "").length : 0;
};
// The world echo of a human message truncates its send time to `.000`; within the
// SAME second the optimistic millisecond stamp is the truer sub-second order, so
// keep whichever carries real sub-second precision. Across DIFFERENT seconds the
// incoming (world) value is authoritative.
const finerTimestamp = (kept: string, incoming: string): string => {
  if (String(kept).slice(0, 19) !== String(incoming).slice(0, 19)) return incoming;
  return subSecondPrecision(kept) >= subSecondPrecision(incoming) ? kept : incoming;
};

const feedItemAnnouncement = (item: unknown, conversationId: string): Announcement | undefined => {
  if (!isRecord(item)) return undefined;
  const feed = item as Partial<ConversationFeedItem>;
  if (!isRecord(feed.artifact)) return undefined;

  const artifact = feed.artifact as Record<string, unknown>;
  const machine = isRecord(artifact.machine) ? artifact.machine : undefined;
  const machineName = String(machine?.name || machine?.id || "");
  if (!machineName) return undefined;

  const scopePath = typeof feed.scopePath === "string" ? feed.scopePath : "";
  const announcementId = String(feed.announcementId || feed.id || machineName);
  const prioritized = isRecord(feed.prioritized)
    ? {
      active: feed.prioritized.active !== false,
      ...(feed.prioritized.id != null ? { id: feed.prioritized.id } : {}),
    }
    : undefined;
  const machineSeed = isRecord(feed.conversationMachine)
    ? feed.conversationMachine
    : { ...(machine || {}), name: machineName };

  const parsed = AnnouncementSchema.safeParse({
    id: announcementId,
    timestamp: typeof feed.timestamp === "string" && feed.timestamp ? feed.timestamp : new Date().toISOString().replace("Z", ""),
    conversationId: feed.conversationId || conversationId || machineName,
    machine: machineSeed,
    sender: "workflow",
    kind: "channel",
    recipient: [],
    refs: scopePath ? [{ path: scopePath }] : [],
    reply: [],
    next: [],
    when: [],
    error: [],
    ...(prioritized ? { prioritized } : {}),
    render: [],
    ref: [],
    artifact: [artifact],
    evidence: [],
    confirmation: [],
  });
  return parsed.success ? parsed.data : undefined;
};

export interface ChannelApi {
  ready: boolean;
  userId: string;
  announcements: Announcement[];
  announce: typeof channel.announce;
  trackArtifactInteraction: typeof channel.trackArtifactInteraction;
  submitArtifactMap: typeof channel.submitArtifactMap;
  query: typeof channel.query;
  prioritizeArtifact: typeof channel.prioritizeArtifact;
  search: typeof channel.search;
  upload: typeof channel.upload;
  uploadFiles: (files: File[], opts: { conversationId: string; metadata?: Record<string, unknown> }) => Promise<void>;
  download: typeof channel.download;
  // content-addressed hydrate: a fingerprint match serves from the cache with
  // zero calls; `force` is the explicit Refresh affordance (one download).
  hydrateArtifact: (artifact: unknown, opts?: { force?: boolean }) => Promise<unknown>;
  // the already-hydrated body for an artifact (filled by the renderer on display);
  // undefined until then. Lets the download save the on-screen artifact, no refetch.
  hydratedArtifactOf: (artifact: unknown) => unknown;
  hydrateAnnouncements: (items: unknown[]) => number;
  // Fill what's missing: fetch + hydrate a conversation's history (row, latest
  // announcements, pins) unless it is already locally known. Idempotent.
  // `refresh` (an EXPLICIT open) ALWAYS refetches and MERGES (upsert — no
  // clearing, no flash): loaded once is not current, and a missed ws frame
  // must not freeze a conversation for the whole session. Returns the fetched
  // history rows ([] when the fetch was skipped) so a caller waiting for a
  // specific row can observe the re-mint.
  ensureConversation: (conversationId: string, seed?: unknown, opts?: { refresh?: boolean }) => Promise<unknown[]>;
  // True when the announcement arrived LIVE on the ws (not history hydration).
  isLiveAnnouncement: (id: string) => boolean;
  // Send a message OPTIMISTICALLY: it appears immediately as a markdown artifact
  // (status start). HTTP acceptance is not recipient/world completion; complete
  // or fail:complete arrives later as a stream delta for the same announcement id.
  sendMessage: (text: string, opts?: { id?: string; conversationId?: string; reply?: Array<{ ref: number | string; label?: string }>; recipient?: unknown }) => Promise<void>;
}

export function useChannel(auth: { userId?: string; token?: string }, activeConversationId = ""): ChannelApi {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [ready, setReady] = useState(false);
  const byId = useRef<Map<string, Announcement>>(new Map());

  const upsert = useCallback((a: Announcement) => {
    const current = byId.current.get(a.id);
    const merged = current?.prioritized && !a.prioritized ? { ...a, prioritized: current.prioritized } : a;
    byId.current.set(merged.id, merged);
    // Chronological: epoch then world serial id — never raw-string localeCompare
    // (which reverses same-second rows; see byChronology).
    setAnnouncements([...byId.current.values()].sort(byChronology));
  }, []);

  // artifact-less replies whose target box has not arrived yet — applied on arrival.
  const pendingFolds = useRef<Map<string, string>>(new Map());
  // prioritized context can arrive separately from the artifact carrier (history windows
  // often contain only later refresh rows). Merge it onto the next matching carrier.
  const pendingPrioritized = useRef<Map<string, { active: boolean; id?: string }>>(new Map());
  // ids that arrived LIVE on the ws (vs. history hydration) — the stream uses this
  // to announce only genuinely new artifact updates.
  const liveIds = useRef<Set<string>>(new Set());
  // conversations whose FULL context (row + history + pins) has been fetched, and
  // conversations with a fetch in flight — the ensureConversation guards. Seeing
  // a single announcement for a conversation (a live frame, a feed projection)
  // does NOT make it loaded: that conflation made opening a conversation skip its
  // history fetch, so you'd see a lone announcement instead of the full thread.
  // ONLY a conversation-context fetch marks loaded.
  // conversation id → the socket GENERATION it was loaded under. While the
  // generation is unchanged the held state is current by construction (every
  // ws frame is kept), so an explicit re-open needs NO refetch; a reconnect
  // bumps the generation and the next open backfills the possible gap. This
  // killed the refetch-on-every-click class (2026-07-23).
  const loadedConversations = useRef<Map<string, number>>(new Map());
  const fetchingConversations = useRef<Set<string>>(new Set());
  // the LAST hydrated body per artifact machine (filled when the renderer
  // hydrates an interactive/surface artifact for display), stamped with the
  // content fingerprint it was fetched for. The download action reads this so
  // it saves what's ALREADY on screen — no refetch, no S3 — and hydrateArtifact
  // serves it on a fingerprint MATCH (zero download for identical content).
  const hydratedArtifacts = useRef<Map<string, { fingerprint: string; artifact: unknown }>>(new Map());
  // SINGLE-FLIGHT per machine: seed re-renders during a demand window fired
  // concurrent downloads that each parked a world worker for the bounded
  // materialization wait — one box saturated the pool and every request 504'd.
  const pendingHydrations = useRef<Map<string, Promise<unknown>>>(new Map());

  // Re-stamp the box's artifact seed (machine.version = the folding reply id) so the
  // renderer re-hydrates the world-refreshed body.
  const stampArtifactVersion = useCallback((target: Announcement, version: string): Announcement => {
    const artifacts = Array.isArray(target.artifact) ? target.artifact : [];
    const seed = artifacts[0];
    if (!seed || typeof seed !== "object") return target;
    const machine = (seed as { machine?: Record<string, unknown> }).machine || {};
    return {
      ...target,
      artifact: [{ ...(seed as object), machine: { ...machine, version } }, ...artifacts.slice(1)],
    } as Announcement;
  }, []);

  const artifactVersionOf = useCallback((target: Announcement): string => {
    const artifacts = Array.isArray(target.artifact) ? target.artifact : [];
    const seed = artifacts[0] as { machine?: { version?: unknown } } | undefined;
    return String(seed?.machine?.version || target.id);
  }, []);

  const artifactRefreshVersionOf = useCallback((source: Announcement): string => {
    const artifacts = Array.isArray(source.artifact) ? source.artifact : [];
    const seed = artifacts[0] as { machine?: { version?: unknown } } | undefined;
    return String(seed?.machine?.version || source.id);
  }, []);

  // The hydration contract: feed/history projections are DEHYDRATED references
  // (artifact.input stripped); only world-pushed/downloaded rows carry input.
  // A dehydrated row must never clobber hydrated data — it may only bump the
  // re-hydrate key.
  const artifactCarriesInput = useCallback((a: Announcement): boolean => {
    const artifacts = Array.isArray(a.artifact) ? a.artifact : [];
    const seed = artifacts[0] as { input?: unknown } | undefined;
    return !!seed && typeof seed === "object" && isRecord((seed as { input?: unknown }).input);
  }, []);

  const versionRank = useCallback((version: string): number =>
    /^\d+$/.test(version) ? Number(version) : Number.NaN, []);

  const timestampVersionRank = useCallback((version: string): number =>
    /^\d{4}-\d{2}-\d{2}T/.test(version) ? Date.parse(version) : Number.NaN, []);

  const freshestVersionFor = useCallback((target: Announcement, version: string): string => {
    const current = artifactVersionOf(target);
    const currentRank = versionRank(current);
    const nextRank = versionRank(version);
    if (Number.isFinite(currentRank) && Number.isFinite(nextRank)) {
      return nextRank >= currentRank ? version : current;
    }
    const currentTimeRank = timestampVersionRank(current);
    const nextTimeRank = timestampVersionRank(version);
    if (Number.isFinite(currentTimeRank) && Number.isFinite(nextTimeRank)) {
      return nextTimeRank >= currentTimeRank ? version : current;
    }
    return version;
  }, [artifactVersionOf, timestampVersionRank, versionRank]);

  const foldArtifactRefresh = useCallback((target: Announcement, source: Announcement): Announcement => {
    const sourceVersion = artifactRefreshVersionOf(source);
    const nextVersion = freshestVersionFor(target, sourceVersion);
    const sourceIsCurrent = nextVersion === sourceVersion;
    // a CURRENT source carrying the hydrated body IS the fresh box — its data
    // merges in place (the re-download is a refresh, not the only carrier). A
    // dehydrated source (a feed/history reference) only bumps the version on
    // the kept target, never clobbers hydrated data.
    const sourceCarriesData = sourceIsCurrent && artifactCarriesInput(source);
    const base = sourceCarriesData || (!target.conversationId && !!source.conversationId)
      ? (source.conversationId ? source : { ...source, conversationId: target.conversationId })
      : target;
    const prioritized = source.prioritized && (sourceIsCurrent || !target.prioritized) ? source.prioritized : target.prioritized;
    // keep the truer sub-second send time: the world echo coarsens it to `.000`,
    // which would drop a live-typed same-second message into world-id order
    // instead of real send order.
    const timestamp = finerTimestamp(target.timestamp, base.timestamp);
    const merged = { ...(prioritized ? { ...base, prioritized } : base), timestamp };
    return stampArtifactVersion(merged, nextVersion);
  }, [artifactCarriesInput, artifactRefreshVersionOf, freshestVersionFor, stampArtifactVersion]);

  const applyPrioritizedContext = useCallback((items: unknown[] | undefined) => {
    if (!Array.isArray(items)) return;
    for (const item of items) {
      const row = item as {
        id?: unknown;
        body?: {
          active?: unknown;
          artifact?: { machine?: { id?: unknown; name?: unknown } };
          conversation?: { id?: unknown };
        };
        active?: unknown;
        artifact?: { machine?: { id?: unknown; name?: unknown } };
        conversationId?: unknown;
        prioritized?: { active?: unknown; id?: unknown };
      };
      const artifactId = String(
        row.body?.artifact?.machine?.id
        ?? row.body?.artifact?.machine?.name
        ?? row.artifact?.machine?.id
        ?? row.artifact?.machine?.name
        ?? "",
      );
      if (!artifactId) continue;
      const active = row.body?.active ?? row.active ?? row.prioritized?.active;
      const prioritized = {
        active: active !== false,
        ...(row.id != null ? { id: String(row.id) } : row.prioritized?.id != null ? { id: String(row.prioritized.id) } : {}),
      };
      const conversationId = String(row.body?.conversation?.id ?? row.conversationId ?? "");
      let applied = false;
      for (const announcement of [...byId.current.values()]) {
        if (announcementArtifactMachineIdOf(announcement) !== artifactId) continue;
        if (conversationId && announcement.conversationId && announcement.conversationId !== conversationId) continue;
        upsert({ ...announcement, prioritized });
        applied = true;
      }
      if (!applied) pendingPrioritized.current.set(artifactId, prioritized);
    }
  }, [upsert]);

  // THE reply rule of the channel: an announcement replying to another WITHOUT an
  // artifact is an UPDATE of the box it replies to — fold it (the box re-hydrates
  // the world-refreshed body), never open a new box. WITH a DIFFERENT artifact it
  // is a literal reply (like a user replying to a specific message) and gets its
  // own box. WITH the SAME artifact machine it is the write/output echo for the
  // replied box, so fold it too.
  const ingest = useCallback((a: Announcement) => {
    // THE HARNESS AUDIT ROUTE (first, before any box/fold logic): an
    // announcement whose ref lane declares sci//harness:audit:@terminal** is
    // one of the artifact's audit ROWS. The pane that renders them lives IN
    // the artifact (the world-owned wrapper, seeded from the served copy and
    // kept live through the artifact bearer) — the shell only makes sure an
    // audit row never opens or folds a stream box (the atom is the
    // declaration, never a shape sniff).
    if (carriesHarnessAudit(a)) return;
    const replies = (a.reply ?? [])
      .map((r) => String((r as { address?: unknown })?.address ?? (r as { id?: unknown })?.id ?? (r as { ref?: unknown })?.ref ?? r))
      .filter(Boolean);
    const artifactCount = Array.isArray(a.artifact) ? a.artifact.length : 0;
    const artifactMachineId = artifactCount > 0 ? announcementArtifactMachineIdOf(a) : "";
    const nextThenRefs = nextThenRefsOf(a);
    // Error boundary (sci/boundaries.md): a fail announcement (announce:@error**
    // — carries error[] and/or a machine fail:* status) is NEVER folded away as
    // an artifact-less reply/continuation. It keeps its OWN visible row.
    const carriesFailure = (a.error?.length ?? 0) > 0
      || String(a.machine?.status || "").startsWith("fail:");
    const prioritized = artifactMachineId ? pendingPrioritized.current.get(artifactMachineId) : undefined;
    const incoming = prioritized && !a.prioritized ? { ...a, prioritized } : a;
    if (prioritized && !a.prioritized) pendingPrioritized.current.delete(artifactMachineId);
    const existingArtifact = artifactMachineId
      ? [...byId.current.values()].find((candidate) =>
        candidate.id !== incoming.id
        && announcementArtifactMachineIdOf(candidate) === artifactMachineId
        && (!candidate.conversationId || !incoming.conversationId || candidate.conversationId === incoming.conversationId),
      )
      : undefined;
    if (existingArtifact) {
      const folded = foldArtifactRefresh(existingArtifact, incoming);
      if (folded.id !== existingArtifact.id) byId.current.delete(existingArtifact.id);
      upsert(folded);
      return;
    }
    // SAME-ID replay of a known artifact row (a history/feed projection or an
    // out-of-order push): a DEHYDRATED copy must not clobber the hydrated box —
    // fold it as a version bump on the kept hydrated row instead of replacing.
    const stored = artifactMachineId ? byId.current.get(incoming.id) : undefined;
    if (stored
      && announcementArtifactMachineIdOf(stored) === artifactMachineId
      && artifactCarriesInput(stored)
      && !artifactCarriesInput(incoming)) {
      upsert(stampArtifactVersion(stored, freshestVersionFor(stored, artifactRefreshVersionOf(incoming))));
      return;
    }
    if (replies.length > 0 && artifactCount === 0 && !carriesFailure) {
      const target = replies.map((ref) => byId.current.get(ref)).find((t): t is Announcement => !!t);
      if (target) {
        upsert(stampArtifactVersion(target, freshestVersionFor(target, a.id)));
      } else {
        for (const ref of replies) pendingFolds.current.set(ref, a.id);
      }
      return;
    }
    if (artifactCount === 0 && !carriesFailure) {
      const target = [...byId.current.values()].find((candidate) => {
        const candidateArtifactCount = Array.isArray(candidate.artifact) ? candidate.artifact.length : 0;
        return candidateArtifactCount > 0 && nextThenRefsOf(candidate).includes(a.id);
      });
      if (target) {
        return;
      }
    }
    if (artifactCount > 0 && nextThenRefs.length > 0) {
      for (const ref of nextThenRefs) {
        const folded = byId.current.get(ref);
        const foldedArtifactCount = Array.isArray(folded?.artifact) ? folded!.artifact.length : 0;
        if (folded && foldedArtifactCount === 0) {
          byId.current.delete(ref);
        }
      }
    }
    upsert(incoming);
    const pending = pendingFolds.current.get(incoming.id);
    if (pending) {
      pendingFolds.current.delete(incoming.id);
      upsert(stampArtifactVersion(incoming, freshestVersionFor(incoming, pending)));
    }
  }, [artifactCarriesInput, artifactRefreshVersionOf, foldArtifactRefresh, freshestVersionFor, stampArtifactVersion, upsert]);

  const hydrateAnnouncements = useCallback((items: unknown[]) => {
    let accepted = 0;
    for (const item of items) {
      const parsed = AnnouncementSchema.safeParse(item);
      if (parsed.success) {
        // ingesting an announcement is NOT loading its conversation — loaded is
        // owned by ensureConversation's context fetch alone, so opening always
        // backfills the full conversation even after a stray frame arrived.
        ingest(parsed.data);
        accepted += 1;
      }
    }
    return accepted;
  }, [ingest]);

  const ensureConversation = useCallback(async (conversationId: string, seed?: unknown, opts?: { refresh?: boolean }): Promise<unknown[]> => {
    const seeded = feedItemAnnouncement(seed, conversationId);
    if (seeded) ingest(seeded);
    // THE SMOOTH RE-MINT (Ori 2026-08-04): an EXPLICIT open (refresh) ALWAYS
    // refetches — the held state renders instantly and the fetch MERGES via
    // the keyed upsert (no clearing, no flash). The epoch-gated "held state
    // IS current by construction" was too strong: a ws frame the transport
    // dropped (the fork completion box arriving minutes late) froze the
    // conversation for the whole socket generation with no way to update it.
    // The 2026-07-23 sluggish-client class was BLOCKING refetch-on-click;
    // this fetch is background + merge, so an open stays instant.
    const loadedEpoch = loadedConversations.current.get(conversationId);
    if (!conversationId
      || (loadedEpoch !== undefined && !opts?.refresh)
      || fetchingConversations.current.has(conversationId)) return [];
    fetchingConversations.current.add(conversationId);
    try {
      const res = await channel.query({ conversationId, limit: 18 });
      const history = res.conversationContext?.history;
      applyPrioritizedContext(res.conversationContext?.prioritized);
      if (Array.isArray(history)) hydrateAnnouncements(history);
      // the context fetch IS the knowledge — an empty conversation needs no refetch.
      loadedConversations.current.set(conversationId, channel.socketEpoch);
      return Array.isArray(history) ? history : [];
      // Boundary rule: a failed context fetch NEVER reads as an empty
      // conversation — the error PROPAGATES so the caller surfaces it (the
      // workspace error state, not "No announcements yet."); only the
      // in-flight marker clears.
    } finally {
      fetchingConversations.current.delete(conversationId);
    }
  }, [applyPrioritizedContext, hydrateAnnouncements]);

  // the store's "is the user watching" input for toast decisions — the fold
  // and the work-completion toasts read it at their own claim moment.
  useEffect(() => {
    feedStore.setActiveConversation(activeConversationId);
  }, [activeConversationId]);

  useEffect(() => {
    if (!auth.userId || !auth.token) {
      setReady(false);
      return;
    }
    const off = channel.on((e) => {
      if (e.kind === "announcement") {
        const a = e.announcement;
        // relevance is OWNED HERE (the socket fires ungated): every frame is
        // KEPT — known-box updates and fold replies land directly, the render
        // path scopes by conversation — and a frame for a conversation with no
        // local history pulls that conversation's context in (backfill).
        liveIds.current.add(a.id);
        ingest(a);
        // SYNC ACCUMULATION (§4): every live frame FOLDS into the channel
        // store's ONE lane classifier — feed/inbox row refreshes fold the
        // served bodies; notify/sync/status frames fold as keyed inbox
        // entries + toasts. NEVER a refetch.
        feedStore.foldLiveAnnouncement(a);
        if (a.conversationId) {
          // live-frame backfill: the frame itself already rendered (ingest
          // above); a failed history backfill is LOUD in the console, not
          // UI-fatal — the next explicit open retries and surfaces it.
          void ensureConversation(a.conversationId).catch((error) => {
            console.error(`[channel] history backfill failed for ${a.conversationId}`, error);
          });
        }
        return;
      }
      // delta = an interim chunk for an existing box; merge the patch.
      const cur = byId.current.get(e.delta.id);
      if (cur && e.delta.patch && typeof e.delta.patch === "object") {
        upsert({ ...cur, ...(e.delta.patch as Partial<Announcement>), id: cur.id });
      }
    });
    channel.connect(auth.userId, auth.token, activeConversationId);
    setReady(true);
    return () => {
      setReady(false);
      off();
      channel.disconnect();
    };
  }, [auth.userId, auth.token, activeConversationId, ensureConversation, ingest, upsert]);

  // OPTIMISTIC send — the message is a markdown artifact the CLIENT creates,
  // puts in its own stream immediately, and announces to world with an EXPLICIT
  // conversation id (no command node, no type switch). The first message into a
  // client-minted id claims the conversation world-side (creator:@human).
  // Reusing the same id lets a retry update the SAME box in place.
  // THE SEND IS A TASK (Ori 2026-07-24): world mints a keyed task from every
  // human message (kind by the conversation's origin — workspace/book/template
  // → @message, an agent conversation → custom) and the task→agent map picks
  // the agents. `opts.agents` is the composer's EXPLICIT picked LIST
  // (sci//task:@agent:@map** is PLURAL — born-assigned carry, evidence
  // data.agent = a list); `opts.tab` declares the origin tab a NEW
  // conversation was minted from (sci//conversation:tab:@tab — evidence
  // data.tab).
  const sendMessage = useCallback(async (text: string, opts: { id?: string; conversationId?: string; reply?: Array<{ ref: number | string; label?: string }>; recipient?: unknown; agents?: string[]; tab?: string } = {}) => {
    if (!opts.conversationId) throw new Error("sendMessage requires a conversationId — the CommandBar mints one for a new workspace");
    const id = opts.id || `local-${Date.now()}`;
    // STABLE timestamp — captured once, so a status update (start→complete|fail) does
    // NOT re-stamp the message later than the agent reply (it must keep its place).
    const timestamp = new Date().toISOString().replace("Z", "");
    const box = (status: "start" | "complete" | "fail:start", errorMessage = ""): Announcement => ({
      id,
      timestamp,
      conversationId: opts.conversationId,
      sender: "human",
      kind: "channel",
      error: errorMessage ? [{ machine: "client.channel", code: `send_failed:${errorMessage}`, positions: [], refs: [] }] : [],
      recipient: Array.isArray(opts.recipient) ? opts.recipient as any : [],
      refs: [],
      // the message shows WHAT it replies to immediately (announcement:reply:@announcement**);
      // world later hydrates the same edge to the full announcement.
      reply: opts.reply ?? [],
      // the box OWNS its machine identity — the world echo folds by THIS id.
      render: [{ mode: "file", suffix: "md", payload: { text }, machine: { name: "message", id, status } }],
    } as Announcement);
    upsert(box("start")); // shows immediately — not waiting for the call
    try {
      await channel.announce({
        conversationId: opts.conversationId,
        // the message's OWN send-time → world stores it as the announcement
        // effective_at, so same-second sends keep send order after a reload
        // (world's serial/clock is the raced processing order, not send order).
        timestamp,
        machine: { name: "message", id, label: "Message", status: "start" },
        render: [{ mode: "file", suffix: "md", payload: { text }, machine: { name: "message", id, status: "start" } }],
        artifact: [{
          // tags required: world's machine-preview gate (control:@machine:@preview)
          // rejects artifact machines without label + non-empty tags.
          machine: { name: "message", id, label: "Message", tags: ["Message", "Chat"], status: "start" },
          suffix: "md",
          position: "match",
          // the artifact IS the message — its text rides input so history
          // reads (artifact-lane shells only) still render the chat.
          input: { text },
          params: [],
        }],
        evidence: [{
          kind: "evidence",
          confidence: 1,
          body: {
            data: {
              text,
              ...(opts.agents?.length ? { agent: opts.agents.map((a) => a.trim()).filter(Boolean) } : {}),
              ...(opts.tab?.trim() ? { tab: opts.tab.trim() } : {}),
              requested: {
                artifact: "message",
                render: [{ mode: "file", suffix: "md" }],
                recipient: opts.recipient ?? [],
              },
            },
          },
        }],
        reply: opts.reply?.map((r) => r.ref),
        recipient: opts.recipient,
      });
      upsert(box("complete")); // accepted — the box leaves 'sending' (the world echo folds in by artifact machine)
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      upsert(box("fail:start", message)); // local channel error: keep the message + surface retry
    }
  }, [upsert]);

  const uploadFiles = useCallback(async (
    files: File[],
    opts: { conversationId: string; metadata?: Record<string, unknown> },
  ) => {
    if (!files.length) return;
    const syncMachine = `sync:${opts.conversationId}:upload:${Date.now().toString(36)}`;
    const uploadTimestamp = new Date().toISOString();
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const totalParts = Math.max(1, Math.ceil(file.size / UPLOAD_CHUNK_BYTES));
      const partId = uploadPartId(opts.conversationId, file, i);
      for (let part = 0; part < totalParts; part++) {
        const start = part * UPLOAD_CHUNK_BYTES;
        const chunk = file.slice(start, Math.min(file.size, start + UPLOAD_CHUNK_BYTES));
        await channel.upload({
          fileName: file.name,
          content: await blobToBase64(chunk),
          contentType: file.type,
          conversationId: opts.conversationId,
          timestamp: uploadTimestamp,
          metadata: {
            ...(opts.metadata || {}),
            conversationId: opts.conversationId,
            ...(totalParts > 1 ? {
              uploadPart: {
                id: partId,
                serial: part + 1,
                total: totalParts,
                fileName: file.name,
                contentType: file.type,
              },
            } : {}),
          },
          sync: { io: "out", machine: syncMachine, total: files.length },
          batch: { serial: i + 1, status: "complete" },
        });
      }
    }
  }, []);

  const prioritizeArtifact = useCallback(async (...args: Parameters<typeof channel.prioritizeArtifact>) => {
    const [input] = args;
    const result = await channel.prioritizeArtifact(...args);
    // the world merged the prioritized backref onto the artifact's announcement ON
    // WRITE — mirror it on the SAME store the stream + prioritized rail derive
    // from (the announcement IS the one conversation object; prioritized is a
    // view of it, never a second list).
    const artifactId = artifactMachineRefOf(input.artifact) || String(input.artifactId || "");
    if (artifactId) {
      const pinId = String((result.row as { id?: unknown } | undefined)?.id ?? "");
      for (const a of [...byId.current.values()]) {
        if (announcementArtifactMachineIdOf(a) !== artifactId) continue;
        if (input.conversationId && a.conversationId && a.conversationId !== input.conversationId) continue;
        upsert({ ...a, prioritized: { active: input.active, ...(pinId ? { id: pinId } : {}) } });
      }
    }
    return result;
  }, [upsert]);

  const surfaceConversationForArtifact = useCallback((artifactId: string): string =>
    artifactId.startsWith("connector_surface_") ? artifactId : "", []);

  const carrierAnnouncementForArtifact = useCallback((artifactId: string, fallback?: Announcement): Announcement | undefined => {
    if (!artifactId) return fallback;
    if (fallback && announcementArtifactMachineIdOf(fallback) === artifactId) return fallback;
    return [...byId.current.values()].find((candidate) => announcementArtifactMachineIdOf(candidate) === artifactId) ?? fallback;
  }, []);

  const announce = useMemo(() => channel.announce.bind(channel), []);
  const trackArtifactInteraction = useCallback((input: ArtifactInteractionInput) => {
    const artifactId = artifactMachineRefOf(input.artifact);
    const surfaceConversationId = surfaceConversationForArtifact(artifactId);
    const carrier = carrierAnnouncementForArtifact(artifactId, input.announcement);
    const carrierArtifactId = carrier ? announcementArtifactMachineIdOf(carrier) : "";
    const carrierMatchesArtifact = !!carrier?.id && (!artifactId || carrierArtifactId === artifactId);
    return channel.trackArtifactInteraction({
      ...input,
      announcement: carrier,
      conversationId: surfaceConversationId || carrier?.conversationId || input.conversationId,
      reply: carrierMatchesArtifact ? [carrier.id] : input.reply,
    });
  }, [carrierAnnouncementForArtifact, surfaceConversationForArtifact]);
  const submitArtifactMap = useCallback((input: ArtifactInteractionInput) => {
    const artifactId = artifactMachineRefOf(input.artifact);
    const surfaceConversationId = surfaceConversationForArtifact(artifactId);
    const carrier = carrierAnnouncementForArtifact(artifactId, input.announcement);
    const carrierArtifactId = carrier ? announcementArtifactMachineIdOf(carrier) : "";
    const carrierMatchesArtifact = !!carrier?.id && (!artifactId || carrierArtifactId === artifactId);
    return channel.submitArtifactMap({
      ...input,
      announcement: carrier,
      conversationId: surfaceConversationId || carrier?.conversationId || input.conversationId,
      reply: carrierMatchesArtifact ? [carrier.id] : input.reply,
    });
  }, [carrierAnnouncementForArtifact, surfaceConversationForArtifact]);
  const query = useCallback(async (...args: Parameters<typeof channel.query>) => {
    const res = await channel.query(...args);
    applyPrioritizedContext(res.conversationContext?.prioritized);
    return res;
  }, [applyPrioritizedContext]);
  const search = useMemo(() => channel.search.bind(channel), []);
  const upload = useMemo(() => channel.upload.bind(channel), []);
  const download = useMemo(() => channel.download.bind(channel), []);
  const hydrateArtifact = useCallback(async (artifact: unknown, opts?: { force?: boolean }) => {
    const a = artifact as { machine?: { id?: string; name?: string } } | undefined;
    const machine = String(a?.machine?.id || a?.machine?.name || "");
    if (!machine) throw new Error("artifact machine id required for hydration");
    // CONTENT-ADDRESSED serve (§2/§4): the seed's content identity decides —
    // a fingerprint MATCH serves the held body with ZERO download calls
    // (memory first, then the IndexedDB cache); a miss makes exactly ONE
    // download. `force` is the explicit Refresh affordance only.
    const fingerprint = artifactSeedFingerprintOf(artifact);
    // CACHE-HIT REVALIDATION (the closed-cache loop, Ori 2026-08-08): a
    // fingerprint match serves the held body instantly — but a browser that
    // opened an older version would otherwise NEVER learn a newer one
    // exists. One cheap content-addressed ask per mount ({unchanged:true}
    // carries no bytes) marks the upgrade store; the CONTROL tab renders the
    // deliberate Upgrade affordance. Never auto-replaces.
    const revalidateHeld = (heldFingerprint: string, heldBody?: unknown) => {
      if (artifactUpgradeStore.known(machine)) return;
      // the ask carries the HELD BODY's own content address, never the seed
      // identity: the seed fingerprint is a cache KEY (shape:version), and a
      // recompose re-claims the row without re-stamping the feed seed — so a
      // seed-hash revalidation re-marked a client that already held the
      // newest body (the eternal false Upgrade mark, 2026-08-09).
      const heldContent = String(
        (heldBody as { shape?: { content?: unknown } } | undefined)?.shape?.content || "",
      ) || heldFingerprint;
      if (!heldContent) return;
      void channel.download({ machine, shape: heldContent })
        .then((res) => {
          const reply = res as {
            unchanged?: boolean;
            artifact?: { unchanged?: boolean };
            upgrade?: { available?: boolean };
          };
          // TWO SIGNALS, ONE REPLY: `unchanged` says the held BYTES are the
          // stored current; the serve's `upgrade` face says the STORED body
          // lags its TEMPLATE (the short reply carries it precisely for this
          // moment — an unchanged holder is exactly who needs the button).
          if (reply?.upgrade?.available === true) {
            artifactUpgradeStore.mark(machine);
            return;
          }
          const unchanged = reply?.unchanged === true || reply?.artifact?.unchanged === true;
          if (!unchanged) artifactUpgradeStore.mark(machine);
        })
        .catch(() => { /* offline/refused — the held body stays; the next mount re-asks */ });
    };
    if (!opts?.force && fingerprint) {
      const held = hydratedArtifacts.current.get(machine);
      if (held && held.fingerprint === fingerprint) {
        revalidateHeld(fingerprint, held.artifact);
        return held.artifact;
      }
      const cached = await getCachedArtifact(machine).catch(() => undefined);
      if (cached?.artifact && cached.fingerprint === fingerprint) {
        hydratedArtifacts.current.set(machine, { fingerprint, artifact: cached.artifact });
        revalidateHeld(fingerprint, cached.artifact);
        return cached.artifact;
      }
    }
    // THE PAGED-BODY DRAIN (the download piece face): a head whose artifact
    // declares sync = {machine, total} carries its html in size-bounded batch
    // pages — drain serial = 1..total through the SAME download door, join in
    // serial order, and append the outlet's configBlock sibling after the
    // joined content (the outlet owns the block; the client only assembles).
    // Size is the ONLY discriminator: an unpaged body declares no sync and
    // skips this entirely.
    const drainPagedBody = async (
      head: Awaited<ReturnType<typeof channel.download>>,
    ): Promise<Awaited<ReturnType<typeof channel.download>>> => {
      const served = head.artifact as
        | { sync?: { total?: unknown }; input?: Record<string, unknown> }
        | undefined;
      const total = Number(served?.sync?.total || 0);
      if (!served || !(total > 0)) return head;
      const pages: string[] = [];
      for (let serial = 1; serial <= total; serial += 1) {
        const page = await channel.download({ machine, serial });
        const pageArtifact = page.artifact as { input?: { html?: unknown } } | undefined;
        const html = typeof pageArtifact?.input?.html === "string" ? pageArtifact.input.html : "";
        if (!html) throw new Error(`artifact ${machine} batch page ${serial}/${total} served no html`);
        pages.push(html);
      }
      const { sync: _declared, ...headArtifact } = served as Record<string, unknown>;
      const joined = pages.join("") + (typeof head.configBlock === "string" ? head.configBlock : "");
      return {
        ...head,
        artifact: {
          ...headArtifact,
          input: { ...(served.input || {}), html: joined },
        },
      };
    };
    if (!opts?.force) {
      const pending = pendingHydrations.current.get(machine);
      if (pending) return pending;
    }
    const flight = (async () => {
    let result: Awaited<ReturnType<typeof channel.download>>;
    try {
      result = await drainPagedBody(await channel.download({ machine }));
    } catch (error) {
      // the DESIGNED offline path: the download crossing failed (offline /
      // network) — serve the last downloaded body, LOUDLY. Never silent: the
      // log names the machine + save time; with no cached body the failure
      // rethrows untouched.
      const cached = await getCachedArtifact(machine).catch(() => undefined);
      if (cached?.artifact) {
        console.warn(
          `[artifact-cache] download failed for ${machine} — serving OFFLINE cached body (saved ${cached.savedAt}, version ${cached.version || "?"})`,
          error,
        );
        hydratedArtifacts.current.set(machine, { fingerprint, artifact: cached.artifact });
        return cached.artifact;
      }
      throw error;
    }
    if (!result.artifact) throw new Error(`artifact body unavailable: ${machine}`);
    hydratedArtifacts.current.set(machine, { fingerprint, artifact: result.artifact });
    // TWO STALE LAYERS, ONE SIGNAL: the serve's own upgrade face says the
    // STORED body lags the CURRENT template (available) — re-downloading the
    // same stale bytes must keep the mark (the "clicked Upgrade, nothing
    // changed" loop); a serve whose face is current clears it.
    const servedUpgrade = (result as { upgrade?: { available?: boolean } }).upgrade;
    if (servedUpgrade?.available === true) artifactUpgradeStore.mark(machine);
    else artifactUpgradeStore.clear(machine);
    // the harness audit lane rides the SAME serve stamped INSIDE the served
    // copy (the outlet config block) — the world-owned wrapper renders it;
    // the shell holds no terminal store.
    // write-through: every successful download refreshes the content-addressed
    // cache under the seed fingerprint that fetched it (the sibling
    // artifactToken rides along — stored, not enforced yet).
    const version = String((result.artifact as { machine?: { version?: unknown } })?.machine?.version ?? "");
    void putCachedArtifact({
      machine,
      artifact: result.artifact,
      version,
      ...(fingerprint ? { fingerprint } : {}),
      ...(typeof result.artifactToken === "string" && result.artifactToken ? { artifactToken: result.artifactToken } : {}),
      savedAt: new Date().toISOString(),
    }).catch((cacheError) => {
      console.warn(`[artifact-cache] write-through failed for ${machine}`, cacheError);
    });
    return result.artifact;
    })();
    pendingHydrations.current.set(machine, flight);
    try {
      return await flight;
    } finally {
      pendingHydrations.current.delete(machine);
    }
  }, []);

  // the artifact body already hydrated for display (interactive html / surface
  // table) keyed by machine — the download action prefers it over the dehydrated
  // announcement seed, so it saves the on-screen artifact without a refetch.
  const hydratedArtifactOf = useCallback((artifact: unknown): unknown => {
    const a = artifact as { machine?: { id?: string; name?: string } } | undefined;
    const machine = a?.machine?.id || a?.machine?.name;
    return machine ? hydratedArtifacts.current.get(String(machine))?.artifact : undefined;
  }, []);

  const isLiveAnnouncement = useCallback((id: string) => liveIds.current.has(id), []);

  return useMemo(() => ({
    ready,
    userId: auth.userId || "",
    announcements,
    announce,
    trackArtifactInteraction,
    submitArtifactMap,
    query,
    prioritizeArtifact,
    search,
    upload,
    uploadFiles,
    download,
    hydrateArtifact,
    hydratedArtifactOf,
    hydrateAnnouncements,
    ensureConversation,
    isLiveAnnouncement,
    sendMessage,
  }), [
    announce,
    announcements,
    auth.userId,
    download,
    ensureConversation,
    hydrateArtifact,
    hydratedArtifactOf,
    hydrateAnnouncements,
    isLiveAnnouncement,
    prioritizeArtifact,
    query,
    ready,
    search,
    sendMessage,
    trackArtifactInteraction,
    upload,
    uploadFiles,
  ]);
}
